// bt-common/engine/policy.ts
// #922 S0a: declarative per-engine send policy. DATA ONLY: nothing evaluates it yet (evaluateSendPolicy,
// gates P/T land in S0b/S1). WA_WEB_POLICY allows everything, so wiring it later changes nothing for WA-Web.
import type { WireMediaKind } from './media-kind';

export const SEND_CONTENT_KINDS = ['text', 'media', 'poll', 'button_reply', 'template', 'reaction', 'forward', 'status', 'group_invite'] as const;
export type SendContentKind = (typeof SEND_CONTENT_KINDS)[number];

export interface MediaPolicy { maxBytes: number; mimetypes: readonly string[] }

export interface EnginePolicy {
    recipients: { groups: boolean; newsletters: boolean; statusBroadcast: boolean; lid: boolean };
    content: {
        kinds: Readonly<Record<SendContentKind, boolean>>;
        media: Readonly<Partial<Record<WireMediaKind, MediaPolicy>>>;
        unknownMediaSize: 'allow' | 'deny';
    };
    outreach: { requiresTemplateOutsideWindow: boolean; customerServiceWindowHours: number | null; optIn: 'block' | 'warn' | 'off' };
    limits: { tierScope: 'portfolio' | null };
    pricing: { metered: boolean };
}

/** WA-Web (extension, Chrome gateway, Baileys): no window, no template requirement, everything allowed. */
export const WA_WEB_POLICY: EnginePolicy = {
    recipients: { groups: true, newsletters: true, statusBroadcast: true, lid: true },
    content: {
        kinds: { text: true, media: true, poll: true, button_reply: true, template: false, reaction: true, forward: true, status: true, group_invite: true },
        media: {},
        unknownMediaSize: 'allow',
    },
    outreach: { requiresTemplateOutsideWindow: false, customerServiceWindowHours: null, optIn: 'off' },
    limits: { tierScope: null },
    pricing: { metered: false },
};

/** Meta Cloud API (plan §4.1). Declared for the exhaustive descriptor table; unused until S1. */
export const CLOUD_API_POLICY: EnginePolicy = {
    recipients: { groups: false, newsletters: false, statusBroadcast: false, lid: true },
    content: {
        kinds: { text: true, media: true, poll: false, button_reply: false, template: true, reaction: true, forward: false, status: false, group_invite: false },
        media: {
            image: { maxBytes: 5 * 1024 * 1024, mimetypes: ['image/jpeg', 'image/png'] },
            video: { maxBytes: 16 * 1024 * 1024, mimetypes: ['video/mp4', 'video/3gpp'] },
            audio: { maxBytes: 16 * 1024 * 1024, mimetypes: ['audio/aac', 'audio/mp4', 'audio/mpeg', 'audio/amr', 'audio/ogg'] },
            document: { maxBytes: 100 * 1024 * 1024, mimetypes: [] },
            sticker: { maxBytes: 500 * 1024, mimetypes: ['image/webp'] },
        },
        unknownMediaSize: 'deny',
    },
    outreach: { requiresTemplateOutsideWindow: true, customerServiceWindowHours: 24, optIn: 'warn' },
    limits: { tierScope: 'portfolio' },
    pricing: { metered: true },
};
